import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  FileText,
  Loader2,
  MessageSquareText,
  Mic,
  Users,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { ApiError, API_BASE } from '../lib/api';
import { EsqueletoLineas } from '../components/ui/Esqueleto';
import {
  diffPalabras,
  NOMBRE_CLASE,
  resultadoApi,
  resultadoKeys,
  tonoDe,
  type ParrafoResultado,
  type Voz,
} from '../lib/resultado';

/**
 * QUÉ PASÓ CON LO QUE DIJISTE — la devolución a quien validó.
 *
 * Tres validadoras del Consejo dejaron noventa y nueve comentarios hablados y
 * no tenían dónde ver en qué terminaron. Para un módulo cuyo argumento entero
 * es la trazabilidad, que la trazabilidad solo la viera quien facilita era una
 * contradicción.
 *
 * Es una página aparte y no una pestaña de la Cabina a propósito: quien validó
 * no es facilitador, no tiene por qué entrar a una consola densa de escritorio,
 * y lo que necesita ver es otra cosa. Acá no se aprueba nada ni se resuelven
 * impactos —eso es trabajo de quien conduce—: acá se lee.
 *
 * Cuatro miradas sobre el mismo material, que es lo que distingue esta pantalla
 * de una lista de comentarios:
 *
 *   Secciones   · el documento por dentro, con lo comentado en su lugar.
 *   Comentarios · lo dicho, en orden, sin el documento de por medio.
 *   Cómo queda  · el antes y el después de cada párrafo tocado.
 *   Implica     · qué OTROS párrafos arrastra cada cambio. Es lo que no se ve
 *                 leyendo comentario por comentario y la razón de todo V5.
 */

/** El detalle que escribió el servidor gana siempre: está redactado para una
 *  persona y sabe qué pasó de verdad. */
function mensajeDeError(e: unknown, respaldo: string): string {
  if (e instanceof ApiError) return e.detail;
  return respaldo;
}

type Vista = 'secciones' | 'comentarios' | 'queda' | 'implica';

const VISTAS: { clave: Vista; nombre: string }[] = [
  { clave: 'secciones', nombre: 'Por sección' },
  { clave: 'comentarios', nombre: 'Comentarios' },
  { clave: 'queda', nombre: 'Cómo queda' },
  { clave: 'implica', nombre: 'Implicaciones' },
];

export function ResultadoPage() {
  const { procesoId } = useParams<{ procesoId: string }>();
  const [params] = useSearchParams();
  const status = useAuthStore((s) => s.status);
  const hydrate = useAuthStore((s) => s.hydrate);

  useEffect(() => {
    if (status === 'idle') void hydrate();
  }, [status, hydrate]);

  if (!procesoId) return <Marco>Link incompleto: falta el proceso.</Marco>;

  if (status === 'idle' || status === 'hydrating') {
    return (
      <Marco>
        <span className="flex items-center justify-center gap-2 text-content-muted">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Verificando tu sesión…
        </span>
      </Marco>
    );
  }
  if (status === 'anonymous') return <Porton procesoId={procesoId} sala={params.get('s') ?? ''} />;

  return <Devolucion procesoId={procesoId} />;
}

/** Marco de marca, el mismo del portal de validación. */
function Marco({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-surface-base">
      <header className="relative bg-navy px-5 pb-5 pt-6">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-brand-gradient" aria-hidden="true" />
        <img src="/brand/logonegativo.png" alt="ÁGORA" className="h-9 w-auto" />
        <p className="mt-2 font-sans text-sm text-white/80">Qué pasó con lo que dijiste</p>
      </header>
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-6">{children}</main>
      <footer className="px-4 pb-6 text-center font-sans text-xs text-content-muted">
        Grupo InteligencIA · cada afirmación conserva quién la dijo
      </footer>
    </div>
  );
}

function Porton({ procesoId, sala }: { procesoId: string; sala: string }) {
  const entrar = () => {
    const p = new URLSearchParams();
    if (sala) p.set('sala', sala);
    p.set('volver', `/resultado/${procesoId}`);
    window.location.href = `${API_BASE}/auth/google/login?${p.toString()}`;
  };
  return (
    <Marco>
      <div className="rounded-xl border border-border bg-surface-raised p-6 text-center shadow-sm">
        <FileText className="mx-auto h-10 w-10 text-navy" aria-hidden="true" />
        <h1 className="mt-3 font-display text-xl font-semibold text-content">
          Mirá en qué quedó tu validación
        </h1>
        <p className="mt-2 font-sans text-sm leading-relaxed text-content-muted">
          Entrá con la misma cuenta con la que comentaste. Vas a ver, párrafo por
          párrafo, qué se dijo, cómo queda redactado y qué otras partes del
          documento quedan tocadas.
        </p>
        <button
          type="button"
          onClick={entrar}
          className="mt-5 w-full rounded-lg bg-navy px-4 py-3 font-sans text-base font-semibold text-white transition hover:bg-navy/90"
        >
          Entrar con Google
        </button>
      </div>
    </Marco>
  );
}

function Devolucion({ procesoId }: { procesoId: string }) {
  const [docSel, setDocSel] = useState<string | null>(null);
  const [persona, setPersona] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>('secciones');
  const [todo, setTodo] = useState(false);

  const indice = useQuery({
    queryKey: resultadoKeys.indice(procesoId),
    queryFn: () => resultadoApi.indice(procesoId),
  });

  // El primer documento con contenido se abre solo: hacer clic para ver algo
  // que ya se podía mostrar es una fricción sin motivo.
  useEffect(() => {
    if (docSel || !indice.data) return;
    const primero = indice.data.documentos.find((d) => d.parrafos > 0) ?? indice.data.documentos[0];
    if (primero) setDocSel(primero.actividad_id);
  }, [indice.data, docSel]);

  const detalle = useQuery({
    queryKey: resultadoKeys.documento(docSel ?? '', persona, !todo),
    queryFn: () => resultadoApi.documento(docSel!, { persona, soloComentados: !todo }),
    enabled: Boolean(docSel),
    retry: false,
  });

  if (indice.isLoading) {
    return (
      <Marco>
        <EsqueletoLineas lineas={6} />
      </Marco>
    );
  }
  if (indice.isError || !indice.data) {
    return (
      <Marco>
        <p className="rounded-xl border border-border bg-surface-raised p-5 font-sans text-sm text-content-muted">
          No pudimos traer la devolución. Tus comentarios están guardados; probá
          de nuevo en un momento.
        </p>
      </Marco>
    );
  }

  const d = indice.data;

  return (
    <Marco>
      <h1 className="font-display text-2xl font-semibold text-content">{d.proceso}</h1>
      <p className="mt-1 font-sans text-sm text-content-muted">
        {d.total_comentarios} comentario{d.total_comentarios === 1 ? '' : 's'} sobre{' '}
        {d.documentos.length} documento{d.documentos.length === 1 ? '' : 's'} ·{' '}
        {d.personas.length} persona{d.personas.length === 1 ? '' : 's'}
      </p>

      {/* ── Filtros ───────────────────────────────────────────────────── */}
      <section className="mt-5 space-y-3 rounded-xl border border-border bg-surface-raised p-4">
        <Filtro etiqueta="Documento">
          {d.documentos.map((x) => (
            <Chip
              key={x.actividad_id}
              activo={x.actividad_id === docSel}
              onClick={() => setDocSel(x.actividad_id)}
            >
              {x.documento ?? x.titulo}
              <span className="ml-1.5 opacity-70">{x.comentarios}</span>
            </Chip>
          ))}
        </Filtro>

        <Filtro etiqueta="Quién">
          <Chip activo={persona === null} onClick={() => setPersona(null)}>
            Todas
          </Chip>
          {d.personas.map((p) => (
            <Chip key={p.nombre} activo={persona === p.nombre} onClick={() => setPersona(p.nombre)}>
              {p.nombre}
              <span className="ml-1.5 opacity-70">{p.comentarios}</span>
            </Chip>
          ))}
        </Filtro>

        <Filtro etiqueta="Ver">
          {VISTAS.map((v) => (
            <Chip key={v.clave} activo={vista === v.clave} onClick={() => setVista(v.clave)}>
              {v.nombre}
            </Chip>
          ))}
        </Filtro>
      </section>

      {/* ── Contenido ─────────────────────────────────────────────────── */}
      {!docSel ? null : detalle.isLoading ? (
        <div className="mt-5">
          <EsqueletoLineas lineas={8} />
        </div>
      ) : detalle.isError ? (
        <div className="mt-5 rounded-xl border border-warning bg-surface-raised p-5">
          <AlertTriangle className="h-5 w-5 text-warning" aria-hidden="true" />
          {/* El mensaje lo manda el servidor, que es el único que sabe cuál de
              los dos casos es. Adivinarlo acá a partir de `publicado` mostraba
              «todavía no se publicó» a quien en realidad estaba viendo una
              validación sin documento cargado: dos problemas distintos, con dos
              soluciones distintas, y el mensaje equivocado manda a la persona a
              esperar algo que no va a llegar. */}
          <p className="mt-2 font-sans text-sm text-content">
            {mensajeDeError(
              detalle.error,
              'No pudimos abrir este documento. Tus comentarios están guardados.',
            )}
          </p>
        </div>
      ) : detalle.data ? (
        <Contenido vista={vista} datos={detalle.data} todo={todo} onTodo={setTodo} />
      ) : null}
    </Marco>
  );
}

function Filtro({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 font-sans text-[11px] font-semibold uppercase tracking-wide text-content-muted">
        {etiqueta}
      </span>
      {children}
    </div>
  );
}

function Chip({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={`inline-flex min-h-touch items-center rounded-full border px-3 font-sans text-xs font-semibold transition-colors duration-fast ${
        activo
          ? 'border-navy bg-navy text-content-inverse'
          : 'border-border bg-surface-raised text-content'
      }`}
    >
      {children}
    </button>
  );
}

function Contenido({
  vista,
  datos,
  todo,
  onTodo,
}: {
  vista: Vista;
  datos: import('../lib/resultado').Resultado;
  todo: boolean;
  onTodo: (v: boolean) => void;
}) {
  const parrafos = useMemo(
    () => datos.secciones.flatMap((s) => s.parrafos.map((p) => ({ seccion: s.titulo, p }))),
    [datos],
  );

  if (vista === 'secciones') {
    return (
      <div className="mt-5 space-y-5">
        <Encabezado datos={datos} todo={todo} onTodo={onTodo} />
        {datos.secciones.map((s) => (
          <section key={s.titulo}>
            <h2 className="border-l-4 border-navy pl-2.5 font-display text-base font-semibold text-content">
              {s.titulo}
              <span className="ml-2 font-sans text-xs font-normal text-content-muted">
                {s.comentarios} comentario{s.comentarios === 1 ? '' : 's'}
              </span>
            </h2>
            <div className="mt-2 space-y-2.5">
              {s.parrafos.map((p) => (
                <Parrafo key={p.bloque_id_estable} p={p} />
              ))}
            </div>
          </section>
        ))}
        <SinUbicar voces={datos.sin_ubicar} />
      </div>
    );
  }

  if (vista === 'comentarios') {
    const voces = parrafos.flatMap(({ seccion, p }) =>
      p.voces.map((v) => ({ seccion, ref: p.ref, v })),
    );
    return (
      <div className="mt-5 space-y-2.5">
        <Encabezado datos={datos} todo={todo} onTodo={onTodo} />
        {voces.map(({ seccion, ref, v }) => (
          <article
            key={v.aporte_id}
            className="rounded-xl border border-l-[3px] border-border bg-surface-raised p-3.5"
            style={{ borderLeftColor: tonoDe(v.autor) }}
          >
            <p className="flex flex-wrap items-center gap-x-2 font-sans text-xs text-content-muted">
              <span className="font-semibold" style={{ color: tonoDe(v.autor) }}>
                {v.autor ?? 'Sin autor'}
              </span>
              {ref && <span className="rounded bg-navy/10 px-1.5 py-0.5 font-semibold text-navy">{ref}</span>}
              <span>{seccion}</span>
              {v.de_viva_voz && <Mic className="h-3 w-3" aria-hidden="true" />}
            </p>
            <p className="mt-1.5 font-sans text-sm leading-relaxed text-content">{v.texto}</p>
          </article>
        ))}
        <SinUbicar voces={datos.sin_ubicar} />
      </div>
    );
  }

  if (vista === 'queda') {
    const tocados = parrafos.filter(({ p }) => p.propuesta || p.final || p.posiciones);
    return (
      <div className="mt-5 space-y-3">
        <Encabezado datos={datos} todo={todo} onTodo={onTodo} />
        {tocados.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-4 font-sans text-sm text-content-muted">
            Todavía no se redactó ninguna propuesta a partir de estos comentarios.
          </p>
        ) : (
          tocados.map(({ p }) => <ComoQueda key={p.bloque_id_estable} p={p} />)
        )}
      </div>
    );
  }

  const conImplicaciones = parrafos.filter(({ p }) => p.implicaciones.length > 0);
  return (
    <div className="mt-5 space-y-3">
      <Encabezado datos={datos} todo={todo} onTodo={onTodo} />
      <p className="font-sans text-sm text-content-muted">
        Cambiar un párrafo casi nunca toca un solo párrafo. Esto es lo que queda
        pendiente de revisar si se aprueba cada cambio.
      </p>
      {conImplicaciones.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-4 font-sans text-sm text-content-muted">
          Ningún cambio de este documento arrastra a otra parte del texto.
        </p>
      ) : (
        conImplicaciones.map(({ p }) => (
          <article key={p.bloque_id_estable} className="rounded-xl border border-border bg-surface-raised p-3.5">
            <p className="font-sans text-sm font-semibold text-content">
              {p.ref ?? `Párrafo ${p.orden}`}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {p.implicaciones.map((i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2.5 py-1 font-sans text-xs font-semibold text-warning">
                  <ArrowRight className="h-3 w-3" aria-hidden="true" />
                  {i}
                </span>
              ))}
            </div>
          </article>
        ))
      )}
    </div>
  );
}

function Encabezado({
  datos,
  todo,
  onTodo,
}: {
  datos: import('../lib/resultado').Resultado;
  todo: boolean;
  onTodo: (v: boolean) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-surface-sunken px-3 py-2">
      <p className="font-sans text-xs text-content-muted">
        <MessageSquareText className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
        {datos.comentarios} comentario{datos.comentarios === 1 ? '' : 's'} ·{' '}
        {datos.parrafos_totales} párrafos en el documento
      </p>
      <label className="inline-flex items-center gap-1.5 font-sans text-xs text-content">
        <input type="checkbox" checked={todo} onChange={(e) => onTodo(e.target.checked)} />
        Ver también lo no comentado
      </label>
    </div>
  );
}

function Parrafo({ p }: { p: ParrafoResultado }) {
  return (
    <article className="rounded-xl border border-border bg-surface-raised p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        {p.ref && (
          <span className="rounded bg-navy/10 px-1.5 py-0.5 font-sans text-[11px] font-semibold text-navy">
            {p.ref}
          </span>
        )}
        {p.clase && (
          <span className="rounded-full bg-surface-sunken px-2 py-0.5 font-sans text-[11px] font-semibold text-content-muted">
            {NOMBRE_CLASE[p.clase] ?? p.clase}
          </span>
        )}
      </div>
      <p className="mt-1.5 font-sans text-sm leading-relaxed text-content">{p.original}</p>
      {p.voces.length > 0 && (
        <ul className="mt-2.5 space-y-2">
          {p.voces.map((v) => (
            <VozLeida key={v.aporte_id} v={v} />
          ))}
        </ul>
      )}
      {/* Cómo queda, acá mismo. Estaba solo en su propia pestaña, y eso obligaba
          a cambiar de vista para contestar la pregunta más obvia que se hace
          quien lee un comentario: ¿y entonces cómo va a decir el documento?
          Va después de las voces porque la propuesta se entiende leyendo antes
          de dónde salió. */}
      {(p.propuesta || p.final || p.clase === 'contradictoria') && (
        <div className="mt-3 rounded-lg bg-surface-sunken p-2.5">
          {p.clase === 'contradictoria' ? (
            <>
              <p className="font-sans text-[11px] font-semibold uppercase tracking-wide text-warning">
                No hay redacción: hay desacuerdo
              </p>
              <p className="mt-1 font-sans text-xs leading-relaxed text-content">
                Se registraron posturas distintas sobre este punto. Decide quien
                conduce la validación, no el sistema.
              </p>
            </>
          ) : (
            <>
              <p className="font-sans text-[11px] font-semibold uppercase tracking-wide text-success">
                {p.final ? 'Queda' : 'Se propone'}
              </p>
              <div className="mt-1 text-xs">
                <DiffTexto antes={p.original} despues={(p.final ?? p.propuesta) ?? ''} />
              </div>
            </>
          )}
          {p.motivo && (
            <p className="mt-1.5 font-sans text-[11px] leading-relaxed text-content-muted">
              {p.motivo}
            </p>
          )}
        </div>
      )}
      <Contexto lineas={p.contexto} />
      {p.implicaciones.length > 0 && (
        <p className="mt-2 font-sans text-[11px] text-warning">
          Si cambia, hay que revisar: {p.implicaciones.join(', ')}
        </p>
      )}
    </article>
  );
}

/** Una voz, con la franja de color de quien la dijo.
 *
 *  El color deja ver de un vistazo que una sección la trabajó una sola persona,
 *  o que dos discreparon sobre el mismo párrafo. Nunca es la única señal: el
 *  nombre va escrito al lado, para quien no distingue los tonos. */
function VozLeida({ v }: { v: Voz }) {
  const tono = tonoDe(v.autor);
  return (
    <li className="border-l-[3px] pl-2.5" style={{ borderColor: tono }}>
      <p className="font-sans text-[11px] font-semibold" style={{ color: tono }}>
        {v.autor ?? 'Sin autor'}
        {v.de_viva_voz && <Mic className="ml-1 inline h-3 w-3" aria-hidden="true" />}
      </p>
      <p className="font-sans text-xs leading-relaxed text-content-muted">{v.texto}</p>
    </li>
  );
}

/** El segmento entero al que pertenece el párrafo comentado.
 *
 *  Un requisito es una fila y se entiende solo; una historia de usuario son doce
 *  —nombre, rol, criterios, validaciones…— y leer una sola no dice nada. Va
 *  cerrado porque el comentario se lee primero; se abre cuando hace falta
 *  entender de qué se está hablando. */
function Contexto({ lineas }: { lineas: import('../lib/resultado').LineaContexto[] }) {
  if (lineas.length === 0) return null;
  return (
    <details className="mt-2.5">
      <summary className="cursor-pointer font-sans text-[11px] font-semibold uppercase tracking-wide text-content-muted">
        Ver todo el segmento ({lineas.length} líneas)
      </summary>
      <dl className="despliega mt-2 space-y-1.5 rounded-lg bg-surface-sunken p-2.5">
        {lineas.map((l) => (
          <div key={l.orden} className={l.comentada ? 'rounded bg-navy/5 px-1.5 py-1' : ''}>
            {l.campo && (
              <dt className="font-sans text-[10px] font-semibold uppercase tracking-wide text-content-muted">
                {l.campo}
              </dt>
            )}
            <dd className="ml-0 whitespace-pre-line font-sans text-xs leading-relaxed text-content">
              {l.texto}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/** El antes y el después, con lo que sale y lo que entra marcado.
 *
 *  Sin esto la propuesta es un párrafo que hay que comparar a ojo con el de
 *  arriba, y en una redacción jurídica de ocho renglones donde cambiaron cuatro
 *  palabras nadie las encuentra. */
function DiffTexto({ antes, despues }: { antes: string; despues: string }) {
  const trozos = diffPalabras(antes, despues);
  return (
    <p className="font-sans text-sm leading-relaxed text-content">
      {trozos.map((t, i) =>
        t.estado === 'igual' ? (
          <span key={i}>{t.texto}</span>
        ) : t.estado === 'sale' ? (
          <span key={i} className="bg-error/10 text-error line-through decoration-error/50">
            {t.texto}
          </span>
        ) : (
          <span key={i} className="bg-success/10 font-medium text-success">
            {t.texto}
          </span>
        ),
      )}
    </p>
  );
}

function ComoQueda({ p }: { p: ParrafoResultado }) {
  const contradictoria = p.clase === 'contradictoria';
  return (
    <article className="rounded-xl border border-border bg-surface-raised p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        {p.ref && (
          <span className="rounded bg-navy/10 px-1.5 py-0.5 font-sans text-[11px] font-semibold text-navy">
            {p.ref}
          </span>
        )}
        <span
          className={`rounded-full px-2 py-0.5 font-sans text-[11px] font-semibold ${
            contradictoria ? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
          }`}
        >
          {NOMBRE_CLASE[p.clase ?? ''] ?? p.clase}
        </span>
      </div>

      <p className="mt-2 font-sans text-[11px] font-semibold uppercase tracking-wide text-content-muted">
        Decía
      </p>
      <p className="font-sans text-sm leading-relaxed text-content-muted">{p.original}</p>

      {contradictoria ? (
        <>
          <p className="mt-3 font-sans text-[11px] font-semibold uppercase tracking-wide text-warning">
            No hay una redacción: hay desacuerdo
          </p>
          <p className="font-sans text-sm leading-relaxed text-content">
            Se registraron posturas distintas sobre este párrafo. La decisión es de
            quien conduce la validación, no del sistema.
          </p>
        </>
      ) : (
        <>
          <p className="mt-3 font-sans text-[11px] font-semibold uppercase tracking-wide text-success">
            {p.final ? 'Queda' : 'Se propone'}
          </p>
          <DiffTexto antes={p.original} despues={(p.final ?? p.propuesta) ?? ''} />
        </>
      )}

      {p.motivo && (
        <p className="mt-2 font-sans text-xs leading-relaxed text-content-muted">{p.motivo}</p>
      )}
      {p.voces.length > 0 && (
        <p className="mt-2 flex flex-wrap items-center gap-1.5 font-sans text-[11px] text-content-muted">
          <Users className="h-3 w-3" aria-hidden="true" />
          A partir de lo que dijo
          {p.voces.map((v) => (
            <span
              key={v.aporte_id}
              className="rounded-full px-1.5 py-0.5 font-semibold"
              style={{ color: tonoDe(v.autor), backgroundColor: 'var(--surface-sunken)' }}
            >
              {v.autor ?? 'alguien'}
            </span>
          ))}
        </p>
      )}
      <Contexto lineas={p.contexto} />
    </article>
  );
}

function SinUbicar({ voces }: { voces: Voz[] }) {
  if (voces.length === 0) return null;
  return (
    <section className="rounded-xl border border-warning bg-surface-raised p-3.5">
      <p className="font-sans text-sm font-semibold text-content">
        {voces.length} comentario{voces.length === 1 ? '' : 's'} sin ubicar
      </p>
      <p className="mt-1 font-sans text-xs text-content-muted">
        Entraron sobre el documento completo, sin señalar un párrafo. Cuentan
        igual: alguien tiene que decidir a qué parte se refieren.
      </p>
      <ul className="mt-2 space-y-1.5">
        {voces.map((v) => (
          <li key={v.aporte_id}>
            <p className="font-sans text-[11px] font-semibold text-content">
              {v.autor ?? 'Sin autor'}
            </p>
            <p className="font-sans text-xs leading-relaxed text-content-muted">{v.texto}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
